(() => {
  const KL_MEDIA_CAROUSEL_SELECTOR = '[data-kl-media-carousel]';
  const KL_MEDIA_CAROUSEL_CAROUSEL_SELECTOR = '[data-kl-media-carousel-carousel]';
  const KL_MEDIA_CAROUSEL_INDEX_SELECTOR = '[data-kl-media-carousel-index]';
  const KL_MEDIA_CAROUSEL_IMAGE_SELECTOR = '[data-kl-media-carousel-image]';

  const KL_MEDIA_CAROUSEL_BADGES_ATTRIBUTE = 'data-kl-product-badges';
  const KL_MEDIA_CAROUSEL_BADGES_HIDDEN_CLASS = 'is-hidden';

  const KL_MEDIA_CAROUSEL_SYNC_EVENTS = 'mounted move updated refresh resized';
  const KL_MEDIA_CAROUSEL_WARM_DISTANCE = 1;
  const KL_MEDIA_CAROUSEL_VARIANT_INPUT_NAME = 'id';

  const klMediaCarousels = new Map();

  function parseMediaIdList(idList) {
    return String(idList || '')
      .split(',')
      .map((mediaId) => mediaId.trim())
      .filter(Boolean);
  }

  function parseVariantMediaMap(variantMediaList) {
    const variantMedia = new Map();

    String(variantMediaList || '')
      .split(',')
      .forEach((pair) => {
        const separatorIndex = pair.indexOf(':');

        if (separatorIndex < 1) return;

        const variantId = pair.slice(0, separatorIndex).trim();
        const mediaId = pair.slice(separatorIndex + 1).trim();

        if (variantId && mediaId) variantMedia.set(variantId, mediaId);
      });

    return variantMedia;
  }

  function getBadgesElement(root) {
    const badgesId = root.dataset.klMediaCarouselBadges;

    if (!badgesId) return null;

    try {
      return document.querySelector(`[${KL_MEDIA_CAROUSEL_BADGES_ATTRIBUTE}="${badgesId}"]`);
    } catch (error) {
      console.debug('kl-media-carousel: could not find the badge row', { badgesId, error });
      return null;
    }
  }

  function getIsSameProductInfo(input, root) {
    const host = input.closest('product-info');

    return !host || host.contains(root);
  }

  function initMediaCarousel(root) {
    if (klMediaCarousels.has(root)) return;

    const carouselElement = root.querySelector(KL_MEDIA_CAROUSEL_CAROUSEL_SELECTOR);

    if (!carouselElement) return;

    const indexElement = root.querySelector(KL_MEDIA_CAROUSEL_INDEX_SELECTOR);
    const badgesElement = getBadgesElement(root);
    const mediaIds = parseMediaIdList(root.dataset.klMediaCarouselMediaIds);
    const variantMedia = parseVariantMediaMap(root.dataset.klMediaCarouselVariantMedia);

    let isBound = false;
    let pendingMediaId = null;

    function updateCounter(carousel) {
      if (!indexElement) return;

      const currentIndex = Number.isFinite(carousel.index) ? carousel.index : 0;

      indexElement.textContent = String(currentIndex + 1);
    }

    function updateBadges(isFirstMediaShowing) {
      if (!badgesElement) return;

      badgesElement.classList.toggle(KL_MEDIA_CAROUSEL_BADGES_HIDDEN_CLASS, !isFirstMediaShowing);
    }

    function warmNeighbours(carousel) {
      try {
        carousel.Components.Slides.forEach((slide) => {
          if (!slide.isWithin(carousel.index, KL_MEDIA_CAROUSEL_WARM_DISTANCE)) return;

          const image = slide.slide.querySelector(KL_MEDIA_CAROUSEL_IMAGE_SELECTOR);

          if (image && image.loading === 'lazy') image.loading = 'eager';
        });
      } catch (error) {
        console.debug('kl-media-carousel: could not warm the neighbouring images', { error });
      }
    }

    function flushPendingMedia(carousel) {
      if (pendingMediaId === null) return;

      const targetIndex = mediaIds.indexOf(pendingMediaId);

      pendingMediaId = null;

      if (targetIndex < 0 || targetIndex === carousel.index) return;

      try {
        carousel.go(targetIndex);
      } catch (error) {
        console.debug('kl-media-carousel: could not move to the variant image', { targetIndex, error });
      }
    }

    function syncCarousel(carousel) {
      updateCounter(carousel);
      updateBadges(!Number.isFinite(carousel.index) || carousel.index === 0);
      warmNeighbours(carousel);
    }

    function bindCarousel() {
      const carousel = carouselElement.carousel;

      if (!carousel || isBound) return;

      isBound = true;

      try {
        carousel.on(KL_MEDIA_CAROUSEL_SYNC_EVENTS, () => syncCarousel(carousel));
        carousel.on('mounted refresh', () => flushPendingMedia(carousel));
        carousel.on('destroy', () => {
          isBound = false;
          updateBadges(true);
        });

        syncCarousel(carousel);
        flushPendingMedia(carousel);
      } catch (error) {
        isBound = false;
        console.debug('kl-media-carousel: could not listen to the carousel', { error });
      }
    }

    function watchForMount() {
      try {
        new MutationObserver(bindCarousel).observe(carouselElement, {
          attributes: true,
          attributeFilter: ['class'],
        });
      } catch (error) {
        console.debug('kl-media-carousel: could not watch the carousel for its mount', { error });
      }
    }

    function goToMedia(mediaId) {
      if (mediaIds.indexOf(String(mediaId)) < 0) return;

      pendingMediaId = String(mediaId);

      const carousel = carouselElement.carousel;

      if (carousel) flushPendingMedia(carousel);
    }

    function handleVariantChange(variantId) {
      const mediaId = variantMedia.get(String(variantId));

      if (mediaId) goToMedia(mediaId);
    }

    klMediaCarousels.set(root, { handleVariantChange });

    watchForMount();
    bindCarousel();
  }

  function forgetDetachedCarousels() {
    klMediaCarousels.forEach((carousel, root) => {
      if (!root.isConnected) klMediaCarousels.delete(root);
    });
  }

  function initMediaCarousels(rootElement) {
    forgetDetachedCarousels();

    rootElement.querySelectorAll(KL_MEDIA_CAROUSEL_SELECTOR).forEach((root) => {
      initMediaCarousel(root);
      watchForCarouselReplacement(root);
    });
  }

  function watchForCarouselReplacement(root) {
    try {
      const host = root.closest('product-info');
      const parent = host && host.parentElement;

      if (!parent || parent.dataset.klMediaCarouselWatched === 'true') return;

      parent.dataset.klMediaCarouselWatched = 'true';

      new MutationObserver(() => initMediaCarousels(document)).observe(parent, { childList: true });
    } catch (error) {
      console.debug('kl-media-carousel: could not watch for a replaced gallery', { error });
    }
  }

  document.addEventListener('change', (event) => {
    const input = event.target;

    if (!input || input.tagName !== 'INPUT' || input.name !== KL_MEDIA_CAROUSEL_VARIANT_INPUT_NAME) return;

    forgetDetachedCarousels();

    klMediaCarousels.forEach((carousel, root) => {
      if (getIsSameProductInfo(input, root)) carousel.handleVariantChange(input.value);
    });
  });

  document.addEventListener('shopify:section:load', (event) => initMediaCarousels(event.target));

  initMediaCarousels(document);
})();
